import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import axios from 'axios';
import apiService, { getPlatformSettings, updatePlatformSettings, addTreatmentPathway, removeTreatmentPathway } from '../../services/api'; // Import apiService for getElevenLabsVoices
import PersonaRepresentationManager from './PersonaRepresentationManager';
import { getTTSConfiguration, convertToLegacyVoiceSettings, updateVoiceSettings } from '../../services/ttsConfigService';
import TextToSpeech from '../TextToSpeech'; // Import TextToSpeech component
// MUI Imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Chip from '@mui/material/Chip';
import Slider from '@mui/material/Slider';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import VoiceSelector from '../common/VoiceSelector';
import AccentSelector from '../common/AccentSelector';
import ImageGenerationSettingsTab from './ImageGenerationSettingsTab';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const PlatformSettingsManager = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [defaultVoiceIdField, setDefaultVoiceIdField] = useState('');
  const [voiceSettings, setVoiceSettings] = useState({});
  const [availableElevenLabsVoices, setAvailableElevenLabsVoices] = useState([]);
  const [availableOnboardingFlows, setAvailableOnboardingFlows] = useState([]);
  const [availableCheckInTemplates, setAvailableCheckInTemplates] = useState([]);
  const [availableAssessments, setAvailableAssessments] = useState([]);
  const [samplePreviewText, setSamplePreviewText] = useState('Hello, this is a voice preview.');

  const [pathwayDialogOpen, setPathwayDialogOpen] = useState(false);
  const [currentPathway, setCurrentPathway] = useState(null);
  const [isEditingPathway, setIsEditingPathway] = useState(false);

  const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
  const [currentPathwayIndex, setCurrentPathwayIndex] = useState(null);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [isEditingPhase, setIsEditingPhase] = useState(false);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(null);

  const fetchSettingsAndVoices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const fetchedSettings = await getPlatformSettings(token);
      setSettings(fetchedSettings);
      
      // Get voice settings from TTS configuration (new centralized location)
      const ttsConfig = await getTTSConfiguration(token);
      if (ttsConfig) {
        // Convert to legacy format for backward compatibility with this UI
        const legacyVoiceSettings = convertToLegacyVoiceSettings(ttsConfig, 'elevenlabs');
        setVoiceSettings(legacyVoiceSettings);
      } else if (fetchedSettings?.voiceSettings) {
        // Fallback to old platform settings if TTS config not available
        setVoiceSettings(fetchedSettings.voiceSettings);
      } else if (fetchedSettings?.defaultElevenLabsVoiceId) {
        // Migrate legacy setting
        setVoiceSettings({
          defaultAITherapistVoiceId: fetchedSettings.defaultElevenLabsVoiceId
        });
      }
      
      // Keep legacy field for backward compatibility
      setDefaultVoiceIdField(fetchedSettings?.defaultElevenLabsVoiceId || '');

      const voices = await apiService.getElevenLabsVoices();
      setAvailableElevenLabsVoices(voices);

      // Fetch available onboarding flows
      try {
        const flowsRes = await axios.get(`${API_BASE_URL}/admin/onboarding-flows`, {
          headers: { 'x-auth-token': token }
        });
        setAvailableOnboardingFlows(flowsRes.data.flows?.filter(f => f.isActive) || []);
      } catch (flowsErr) {
        console.error('Error fetching onboarding flows:', flowsErr);
        // Non-critical - continue without flows
      }

      // Fetch available check-in templates
      try {
        const templatesRes = await axios.get(`${API_BASE_URL}/check-in-templates`, {
          headers: { 'x-auth-token': token }
        });
        setAvailableCheckInTemplates(templatesRes.data.templates?.filter(t => t.isActive) || []);
      } catch (templatesErr) {
        console.error('Error fetching check-in templates:', templatesErr);
        // Non-critical - continue without templates
      }

      // Fetch available assessments (test definitions)
      try {
        const assessmentsRes = await axios.get(`${API_BASE_URL}/assessments/definitions`, {
          headers: { 'x-auth-token': token }
        });
        setAvailableAssessments(assessmentsRes.data || []);
      } catch (assessmentsErr) {
        console.error('Error fetching assessments:', assessmentsErr);
        // Non-critical - continue without assessments
      }

    } catch (err) {
      setError(err.message || 'Failed to load initial settings or voices');
      console.error('Error fetching initial data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]); 

  useEffect(() => {
    if (token) {
      fetchSettingsAndVoices();
    }
  }, [token, fetchSettingsAndVoices]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleUpdateSettings = async (section, updatedData) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const newSettingsPayload = { ...settings }; 

      if (section === 'treatmentPathways') {
        newSettingsPayload.treatmentPathways = updatedData;
      } else if (section === 'sessionStructure' || section === 'reflectionSettings' || section === 'aiResponseDelaySettings' || section === 'onboardingConfiguration' || section === 'gen2Configuration' || section === 'voiceModeConfiguration' || section === 'voiceAndAvatar' || section === 'imageGenerationConfiguration' || section === 'clientUi') {
        newSettingsPayload[section] = {
          ...(settings[section] || {}),
          ...updatedData
        };
      } else {
        newSettingsPayload[section] = updatedData;
      }
      
      const updatedSettingsResponse = await updatePlatformSettings(newSettingsPayload, token); 
      
      setSettings(updatedSettingsResponse.settings); 
      if (section === 'defaultElevenLabsVoiceId') {
        setDefaultVoiceIdField(updatedData);
      }
      if (section === 'voiceSettings') {
        // Also update the new TTS configuration
        try {
          await updateVoiceSettings(token, updatedData, 'elevenlabs');
        } catch (ttsError) {
          console.warn('Could not update TTS configuration:', ttsError);
          // Continue anyway - platform settings were updated
        }
        setVoiceSettings(updatedData);
      }

      setSuccess(`${section.replace(/([A-Z])/g, ' $1').trim().charAt(0).toUpperCase() + section.replace(/([A-Z])/g, ' $1').trim().slice(1)} settings updated successfully!`);
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || `Failed to update ${section.replace(/([A-Z])/g, ' $1').trim()} settings`);
      console.error(`Error updating ${section.replace(/([A-Z])/g, ' $1').trim()} settings:`, err);
    } finally {
      setLoading(false);
    }
  };

  const SessionStructureSettings = () => {
    const [formData, setFormData] = useState({
      defaultSessionLength: settings?.sessionStructure?.defaultSessionLength || 45,
      defaultSessionFrequency: settings?.sessionStructure?.defaultSessionFrequency || 'weekly',
      enforceTimeBoundaries: settings?.sessionStructure?.enforceTimeBoundaries || false,
      sessionStartPrompt: settings?.sessionStructure?.sessionStartPrompt || '',
      sessionEndPrompt: settings?.sessionStructure?.sessionEndPrompt || '',
      closeSessionAutomatically: settings?.sessionStructure?.closeSessionAutomatically || true,
    });

    const handleChange = (e) => {
      const { name, value, checked } = e.target;
      setFormData({
        ...formData,
        [name]: e.target.type === 'checkbox' ? checked : value,
      });
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      handleUpdateSettings('sessionStructure', formData);
    };

    return (
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 600, margin: 'auto' }}>
        <Typography variant="h6" gutterBottom>Session Duration and Frequency</Typography>
        <FormControl fullWidth margin="normal">
          <InputLabel id="default-session-length-label">Default Session Length</InputLabel>
          <Select labelId="default-session-length-label" name="defaultSessionLength" value={formData.defaultSessionLength} onChange={handleChange} label="Default Session Length">
            <MenuItem value={30}>30 minutes</MenuItem>
            <MenuItem value={45}>45 minutes</MenuItem>
            <MenuItem value={60}>60 minutes</MenuItem>
            <MenuItem value={90}>90 minutes</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel id="default-session-frequency-label">Default Session Frequency</InputLabel>
          <Select labelId="default-session-frequency-label" name="defaultSessionFrequency" value={formData.defaultSessionFrequency} onChange={handleChange} label="Default Session Frequency">
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="biweekly">Bi-weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel control={<Switch checked={formData.enforceTimeBoundaries} onChange={handleChange} name="enforceTimeBoundaries" color="primary" />} label="Enforce Time Boundaries" sx={{ mt: 2, display: 'block' }} />
        <FormControlLabel control={<Switch checked={formData.closeSessionAutomatically} onChange={handleChange} name="closeSessionAutomatically" color="primary" />} label="Close Sessions Automatically at End Time" sx={{ mt: 1, display: 'block', mb: 2 }} />
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Session Prompts</Typography>
        <TextField fullWidth margin="normal" name="sessionStartPrompt" label="Session Start Prompt" value={formData.sessionStartPrompt} onChange={handleChange} multiline rows={3} placeholder="Message shown to client at the start of a session" />
        <TextField fullWidth margin="normal" name="sessionEndPrompt" label="Session End Prompt" value={formData.sessionEndPrompt} onChange={handleChange} multiline rows={3} placeholder="Message shown to client at the end of a session" />
        <Button type="submit" variant="contained" color="primary" sx={{ mt: 3 }} disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save Session Settings'}
        </Button>
      </Box>
    );
  };

  const TreatmentPathwaysManager = () => {
    const pathways = settings?.treatmentPathways || [];
    const [phaseTab, setPhaseTab] = useState(0);

    // Default phase structure with all fields
    const getDefaultPhase = () => ({
      name: '',
      description: '',
      defaultDuration: 4,
      phasePrompt: '',
      aiAutonomyLevel: 'full',
      requiresHumanApprovalToEnter: false,
      allowedAssessments: [],
      restrictedTopics: [],
      checkInTemplateId: null,
      safetyMonitoring: {
        enabled: false,
        sudsThreshold: 7,
        autoGroundingEnabled: false,
        escalationTriggers: [],
        escalateToTherapist: true
      },
      readinessCriteria: [],
      autoTransition: {
        enabled: false,
        minDurationDays: 7,
        readinessThreshold: 80,
        checkFrequency: 'daily',
        notifyTherapist: true,
        therapistGracePeriodHours: 24,
        autoAdvanceAfterGrace: false
      }
    });

    const handlePathwayDialogOpen = (pathway = null) => {
      setCurrentPathway(pathway || {
        name: '',
        description: '',
        isDefault: false,
        key: '',
        suitableModalities: [],
        checkInTemplateId: null,
        phases: []
      });
      setIsEditingPathway(!!pathway);
      setPathwayDialogOpen(true);
    };

    const handlePathwayDialogClose = () => {
      setPathwayDialogOpen(false);
      setCurrentPathway(null);
    };

    const handlePhaseDialogOpen = (pathwayIndex, phase = null, phaseIndex = null) => {
      setCurrentPathwayIndex(pathwayIndex);
      setCurrentPhase(phase ? { ...getDefaultPhase(), ...phase } : getDefaultPhase());
      setIsEditingPhase(!!phase);
      setCurrentPhaseIndex(phaseIndex);
      setPhaseTab(0);
      setPhaseDialogOpen(true);
    };

    const handlePhaseDialogClose = () => {
      setPhaseDialogOpen(false);
      setCurrentPhase(null);
      setCurrentPhaseIndex(null);
      setPhaseTab(0);
    };

    const handleSavePathway = async () => {
      if (!currentPathway.name || !currentPathway.description) {
        setError('Pathway name and description are required');
        return;
      }
      setLoading(true);
      setError('');
      try {
        if (isEditingPathway) {
          const updatedPathways = [...pathways];
          const pathwayIndex = updatedPathways.findIndex(p => p.name === currentPathway.name);
          if (pathwayIndex >= 0) {
            updatedPathways[pathwayIndex] = currentPathway;
          }
          await handleUpdateSettings('treatmentPathways', updatedPathways);
        } else {
          await addTreatmentPathway(currentPathway, token);
          fetchSettingsAndVoices();
        }
        setSuccess(isEditingPathway ? 'Pathway updated successfully!' : 'New pathway added successfully!');
        handlePathwayDialogClose();
      } catch (err) {
        setError(err.message || 'Failed to save pathway');
      } finally {
        setLoading(false);
      }
    };

    const handleRemovePathway = async (pathwayName) => {
      if (window.confirm(`Are you sure you want to remove the "${pathwayName}" pathway?`)) {
        setLoading(true);
        setError('');
        try {
          await removeTreatmentPathway(pathwayName, token);
          fetchSettingsAndVoices();
          setSuccess('Pathway removed successfully!');
        } catch (err) {
          setError(err.message || 'Failed to remove pathway');
        } finally {
          setLoading(false);
        }
      }
    };

    const handleSavePhase = () => {
      if (!currentPhase.name || !currentPhase.description || !currentPhase.phasePrompt) {
        setError('Phase name, description, and prompt are required');
        return;
      }
      const updatedPathways = [...pathways];
      const pathway = updatedPathways[currentPathwayIndex];
      if (isEditingPhase) {
        pathway.phases[currentPhaseIndex] = currentPhase;
      } else {
        pathway.phases.push(currentPhase);
      }
      handleUpdateSettings('treatmentPathways', updatedPathways);
      handlePhaseDialogClose();
    };

    const handleRemovePhase = (pathwayIndex, phaseIndex) => {
      if (window.confirm('Are you sure you want to remove this phase?')) {
        const updatedPathways = [...pathways];
        updatedPathways[pathwayIndex].phases.splice(phaseIndex, 1);
        handleUpdateSettings('treatmentPathways', updatedPathways);
      }
    };

    const handleSetDefaultPathway = (pathwayName) => {
      const updatedPathways = pathways.map(pathway => ({
        ...pathway,
        isDefault: pathway.name === pathwayName
      }));
      handleUpdateSettings('treatmentPathways', updatedPathways);
    };

    // Helper to update nested phase fields
    const updatePhaseField = (field, value) => {
      setCurrentPhase(prev => ({ ...prev, [field]: value }));
    };

    const updateSafetyMonitoring = (field, value) => {
      setCurrentPhase(prev => ({
        ...prev,
        safetyMonitoring: { ...prev.safetyMonitoring, [field]: value }
      }));
    };

    const updateAutoTransition = (field, value) => {
      setCurrentPhase(prev => ({
        ...prev,
        autoTransition: { ...prev.autoTransition, [field]: value }
      }));
    };

    // Escalation trigger options
    const escalationTriggerOptions = [
      'suicidal_ideation',
      'self_harm',
      'dissociation',
      'severe_distress',
      'panic_attack',
      'psychotic_symptoms',
      'substance_crisis',
      'aggressive_behavior'
    ];

    // Common restricted topics
    const restrictedTopicOptions = [
      'trauma_processing',
      'exposure_exercises',
      'trauma_narrative',
      'detailed_trauma_discussion',
      'cognitive_processing',
      'emdr_techniques',
      'grief_processing',
      'relationship_conflicts'
    ];

    // Modality options
    const modalityOptions = [
      'cbt',
      'dbt',
      'trauma',
      'ptsd',
      'anxiety',
      'depression',
      'adhd',
      'eating_disorders',
      'substance_use',
      'grief',
      'relationship',
      'mindfulness',
      'psychodynamic',
      'mbt',
      'act'
    ];

    // Get autonomy level display info
    const getAutonomyLevelInfo = (level) => {
      switch (level) {
        case 'full':
          return { color: 'success', label: 'Full', description: 'AI operates independently within phase guidelines' };
        case 'supervised':
          return { color: 'warning', label: 'Supervised', description: 'AI follows therapist treatment plan, reports progress' };
        case 'restricted':
          return { color: 'error', label: 'Restricted', description: 'AI limited to homework support, grounding, psychoeducation only' };
        default:
          return { color: 'default', label: 'Unknown', description: '' };
      }
    };

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Treatment Pathways</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handlePathwayDialogOpen()}>
            Add New Pathway
          </Button>
        </Box>

        {pathways.length === 0 ? (
          <Alert severity="info">No treatment pathways defined yet.</Alert>
        ) : (
          pathways.map((pathway, pathwayIndex) => (
            <Accordion key={pathway.name || pathwayIndex} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: pathway.isDefault ? 'bold' : 'normal' }}>
                      {pathway.name} {pathway.isDefault && <span style={{ color: 'green' }}>(Default)</span>}
                    </Typography>
                    {pathway.suitableModalities?.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        {pathway.suitableModalities.slice(0, 3).map(mod => (
                          <Chip key={mod} label={mod} size="small" variant="outlined" />
                        ))}
                        {pathway.suitableModalities.length > 3 && (
                          <Chip label={`+${pathway.suitableModalities.length - 3}`} size="small" />
                        )}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handlePathwayDialogOpen(pathway); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleRemovePathway(pathway.name); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    {!pathway.isDefault && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => { e.stopPropagation(); handleSetDefaultPathway(pathway.name); }}
                        sx={{ ml: 1 }}
                      >
                        Set as Default
                      </Button>
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" paragraph>{pathway.description}</Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2">Pathway Phases ({pathway.phases?.length || 0})</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => handlePhaseDialogOpen(pathwayIndex)}>
                    Add Phase
                  </Button>
                </Box>
                {!pathway.phases?.length ? (
                  <Alert severity="info" sx={{ mb: 2 }}>No phases defined yet.</Alert>
                ) : (
                  <Grid container spacing={2}>
                    {pathway.phases.map((phase, phaseIndex) => {
                      const autonomyInfo = getAutonomyLevelInfo(phase.aiAutonomyLevel);
                      return (
                        <Grid item xs={12} sm={6} md={4} key={`${pathway.name}-${phase.name}-${phaseIndex}`}>
                          <Card variant="outlined">
                            <CardHeader
                              title={phase.name}
                              subheader={`Duration: ${phase.defaultDuration} weeks`}
                              action={
                                <Chip
                                  label={autonomyInfo.label}
                                  color={autonomyInfo.color}
                                  size="small"
                                />
                              }
                            />
                            <CardContent>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {phase.description}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {phase.requiresHumanApprovalToEnter && (
                                  <Chip label="Approval Required" size="small" color="warning" variant="outlined" />
                                )}
                                {phase.safetyMonitoring?.enabled && (
                                  <Chip label="Safety Monitoring" size="small" color="error" variant="outlined" />
                                )}
                                {phase.autoTransition?.enabled && (
                                  <Chip label="Auto-Transition" size="small" color="info" variant="outlined" />
                                )}
                              </Box>
                            </CardContent>
                            <CardActions disableSpacing>
                              <IconButton size="small" onClick={() => handlePhaseDialogOpen(pathwayIndex, phase, phaseIndex)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleRemovePhase(pathwayIndex, phaseIndex)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </CardActions>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}

        {/* Pathway Dialog */}
        <Dialog open={pathwayDialogOpen} onClose={handlePathwayDialogClose} maxWidth="md" fullWidth>
          <DialogTitle>{isEditingPathway ? 'Edit Treatment Pathway' : 'Add New Treatment Pathway'}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Pathway Name"
              value={currentPathway?.name || ''}
              onChange={(e) => setCurrentPathway({ ...currentPathway, name: e.target.value })}
              disabled={isEditingPathway}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Pathway Key (optional)"
              value={currentPathway?.key || ''}
              onChange={(e) => setCurrentPathway({ ...currentPathway, key: e.target.value })}
              helperText="Unique identifier for API references (e.g., 'trauma-ptsd')"
            />
            <TextField
              fullWidth
              margin="normal"
              label="Description"
              value={currentPathway?.description || ''}
              onChange={(e) => setCurrentPathway({ ...currentPathway, description: e.target.value })}
              multiline
              rows={3}
              required
            />

            <Autocomplete
              multiple
              options={modalityOptions}
              value={currentPathway?.suitableModalities || []}
              onChange={(e, newValue) => setCurrentPathway({ ...currentPathway, suitableModalities: newValue })}
              freeSolo
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  label="Suitable Modalities"
                  helperText="Therapist specializations this pathway is suited for"
                />
              )}
              sx={{ mt: 2 }}
            />

            <FormControl fullWidth margin="normal">
              <InputLabel id="pathway-checkin-template-label">Default Check-In Template</InputLabel>
              <Select
                labelId="pathway-checkin-template-label"
                value={currentPathway?.checkInTemplateId || ''}
                onChange={(e) => setCurrentPathway({ ...currentPathway, checkInTemplateId: e.target.value || null })}
                label="Default Check-In Template"
              >
                <MenuItem value=""><em>None (use system default)</em></MenuItem>
                {availableCheckInTemplates.map(template => (
                  <MenuItem key={template._id} value={template._id}>
                    {template.displayName || template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={currentPathway?.isDefault || false}
                  onChange={(e) => setCurrentPathway({ ...currentPathway, isDefault: e.target.checked })}
                  color="primary"
                />
              }
              label="Set as Default Pathway"
              sx={{ mt: 2, display: 'block' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePathwayDialogClose} color="primary">Cancel</Button>
            <Button onClick={handleSavePathway} color="primary" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Save Pathway'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Enhanced Phase Dialog */}
        <Dialog open={phaseDialogOpen} onClose={handlePhaseDialogClose} maxWidth="lg" fullWidth>
          <DialogTitle>{isEditingPhase ? 'Edit Phase' : 'Add New Phase'}</DialogTitle>
          <DialogContent>
            <Tabs value={phaseTab} onChange={(e, newValue) => setPhaseTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tab label="Basic Info" />
              <Tab label="AI Behavior" />
              <Tab label="Safety" />
              <Tab label="Transitions" />
              <Tab label="Readiness Criteria" />
            </Tabs>

            {/* Tab 0: Basic Info */}
            {phaseTab === 0 && (
              <Box>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Phase Name"
                  value={currentPhase?.name || ''}
                  onChange={(e) => updatePhaseField('name', e.target.value)}
                  required
                  placeholder="e.g., Phase 1: Stabilization"
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Description"
                  value={currentPhase?.description || ''}
                  onChange={(e) => updatePhaseField('description', e.target.value)}
                  multiline
                  rows={2}
                  required
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Default Duration (weeks)"
                  type="number"
                  InputProps={{ inputProps: { min: 1, max: 52 } }}
                  value={currentPhase?.defaultDuration || 4}
                  onChange={(e) => updatePhaseField('defaultDuration', parseInt(e.target.value, 10))}
                  required
                />
                <TextField
                  fullWidth
                  margin="normal"
                  label="Phase Prompt"
                  value={currentPhase?.phasePrompt || ''}
                  onChange={(e) => updatePhaseField('phasePrompt', e.target.value)}
                  multiline
                  rows={6}
                  helperText="Detailed instructions for the AI about therapeutic approach during this phase"
                  required
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel id="phase-checkin-template-label">Check-In Template</InputLabel>
                  <Select
                    labelId="phase-checkin-template-label"
                    value={currentPhase?.checkInTemplateId || ''}
                    onChange={(e) => updatePhaseField('checkInTemplateId', e.target.value || null)}
                    label="Check-In Template"
                  >
                    <MenuItem value=""><em>Use pathway default</em></MenuItem>
                    {availableCheckInTemplates.map(template => (
                      <MenuItem key={template._id} value={template._id}>
                        {template.displayName || template.name} ({template.category})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Tab 1: AI Behavior */}
            {phaseTab === 1 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>AI Autonomy Level</Typography>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="ai-autonomy-label">AI Autonomy Level</InputLabel>
                  <Select
                    labelId="ai-autonomy-label"
                    value={currentPhase?.aiAutonomyLevel || 'full'}
                    onChange={(e) => updatePhaseField('aiAutonomyLevel', e.target.value)}
                    label="AI Autonomy Level"
                  >
                    <MenuItem value="full">
                      <Box>
                        <Typography>Full</Typography>
                        <Typography variant="caption" color="text.secondary">
                          AI operates independently within phase guidelines
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="supervised">
                      <Box>
                        <Typography>Supervised</Typography>
                        <Typography variant="caption" color="text.secondary">
                          AI follows therapist treatment plan, reports progress
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="restricted">
                      <Box>
                        <Typography>Restricted</Typography>
                        <Typography variant="caption" color="text.secondary">
                          AI limited to homework support, grounding, psychoeducation only
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                  {currentPhase?.aiAutonomyLevel === 'full' && 'The AI will make clinical judgments independently, following the phase prompt guidelines.'}
                  {currentPhase?.aiAutonomyLevel === 'supervised' && 'The AI will follow a therapist-defined treatment plan and report progress to the therapist.'}
                  {currentPhase?.aiAutonomyLevel === 'restricted' && 'The AI is limited to supportive activities only. All clinical work should be done by the human therapist.'}
                </Alert>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle2" gutterBottom>Content Restrictions</Typography>

                <Autocomplete
                  multiple
                  options={restrictedTopicOptions}
                  value={currentPhase?.restrictedTopics || []}
                  onChange={(e, newValue) => updatePhaseField('restrictedTopics', newValue)}
                  freeSolo
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="outlined" label={option} color="error" {...getTagProps({ index })} key={option} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="normal"
                      label="Restricted Topics"
                      helperText="Topics the AI should NOT initiate discussion about in this phase"
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={availableAssessments.map(a => a.testId || a.id)}
                  value={currentPhase?.allowedAssessments || []}
                  onChange={(e, newValue) => updatePhaseField('allowedAssessments', newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="normal"
                      label="Allowed Assessments"
                      helperText="Leave empty to allow all assessments. Select specific assessments to restrict which can be administered."
                    />
                  )}
                />
              </Box>
            )}

            {/* Tab 2: Safety */}
            {phaseTab === 2 && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentPhase?.safetyMonitoring?.enabled || false}
                      onChange={(e) => updateSafetyMonitoring('enabled', e.target.checked)}
                    />
                  }
                  label="Enable Safety Monitoring"
                  sx={{ mb: 2 }}
                />

                {currentPhase?.safetyMonitoring?.enabled && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>SUDS Threshold (0-10)</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      When client distress reaches or exceeds this level, trigger safety interventions.
                    </Typography>
                    <Slider
                      value={currentPhase?.safetyMonitoring?.sudsThreshold ?? 7}
                      onChange={(e, newValue) => updateSafetyMonitoring('sudsThreshold', newValue)}
                      min={0}
                      max={10}
                      marks
                      valueLabelDisplay="on"
                      sx={{ mb: 3 }}
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={currentPhase?.safetyMonitoring?.autoGroundingEnabled || false}
                          onChange={(e) => updateSafetyMonitoring('autoGroundingEnabled', e.target.checked)}
                        />
                      }
                      label="Enable Automatic Grounding Protocol"
                      sx={{ display: 'block', mb: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                      When enabled, the AI will automatically offer grounding exercises when distress is high.
                    </Typography>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={currentPhase?.safetyMonitoring?.escalateToTherapist ?? true}
                          onChange={(e) => updateSafetyMonitoring('escalateToTherapist', e.target.checked)}
                        />
                      }
                      label="Escalate to Therapist"
                      sx={{ display: 'block', mb: 2 }}
                    />

                    <Autocomplete
                      multiple
                      options={escalationTriggerOptions}
                      value={currentPhase?.safetyMonitoring?.escalationTriggers || []}
                      onChange={(e, newValue) => updateSafetyMonitoring('escalationTriggers', newValue)}
                      freeSolo
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip variant="outlined" label={option} color="error" {...getTagProps({ index })} key={option} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          margin="normal"
                          label="Escalation Triggers"
                          helperText="Keywords/situations that trigger immediate escalation"
                        />
                      )}
                    />
                  </>
                )}
              </Box>
            )}

            {/* Tab 3: Transitions */}
            {phaseTab === 3 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Phase Entry Requirements</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentPhase?.requiresHumanApprovalToEnter || false}
                      onChange={(e) => updatePhaseField('requiresHumanApprovalToEnter', e.target.checked)}
                    />
                  }
                  label="Require Human Approval to Enter This Phase"
                  sx={{ display: 'block', mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 3 }}>
                  When enabled, a therapist must approve before a client can transition into this phase.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle2" gutterBottom>Automatic Phase Transitions</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentPhase?.autoTransition?.enabled || false}
                      onChange={(e) => updateAutoTransition('enabled', e.target.checked)}
                    />
                  }
                  label="Enable Automatic Transitions FROM This Phase"
                  sx={{ display: 'block', mb: 2 }}
                />

                {currentPhase?.autoTransition?.enabled && (
                  <Box sx={{ ml: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Minimum Days in Phase"
                          value={currentPhase?.autoTransition?.minDurationDays ?? 7}
                          onChange={(e) => updateAutoTransition('minDurationDays', parseInt(e.target.value, 10))}
                          InputProps={{ inputProps: { min: 1 } }}
                          helperText="Client must be in phase at least this many days"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Readiness Threshold (%)"
                          value={currentPhase?.autoTransition?.readinessThreshold ?? 80}
                          onChange={(e) => updateAutoTransition('readinessThreshold', parseInt(e.target.value, 10))}
                          InputProps={{ inputProps: { min: 50, max: 100 } }}
                          helperText="Required readiness score to trigger transition"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel id="check-frequency-label">Check Frequency</InputLabel>
                          <Select
                            labelId="check-frequency-label"
                            value={currentPhase?.autoTransition?.checkFrequency || 'daily'}
                            onChange={(e) => updateAutoTransition('checkFrequency', e.target.value)}
                            label="Check Frequency"
                          >
                            <MenuItem value="daily">Daily</MenuItem>
                            <MenuItem value="weekly">Weekly</MenuItem>
                            <MenuItem value="on_checkin">On Check-In Submission</MenuItem>
                            <MenuItem value="on_assessment">On Assessment Completion</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Therapist Grace Period (hours)"
                          value={currentPhase?.autoTransition?.therapistGracePeriodHours ?? 24}
                          onChange={(e) => updateAutoTransition('therapistGracePeriodHours', parseInt(e.target.value, 10))}
                          InputProps={{ inputProps: { min: 0, max: 168 } }}
                          helperText="Hours to wait for therapist review"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={currentPhase?.autoTransition?.notifyTherapist ?? true}
                              onChange={(e) => updateAutoTransition('notifyTherapist', e.target.checked)}
                            />
                          }
                          label="Notify Therapist Before Auto-Transition"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={currentPhase?.autoTransition?.autoAdvanceAfterGrace || false}
                              onChange={(e) => updateAutoTransition('autoAdvanceAfterGrace', e.target.checked)}
                            />
                          }
                          label="Auto-Advance if Therapist Doesn't Respond"
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                          If enabled and therapist doesn't respond within grace period, transition proceeds automatically.
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            )}

            {/* Tab 4: Readiness Criteria */}
            {phaseTab === 4 && (
              <ReadinessCriteriaEditor
                criteria={currentPhase?.readinessCriteria || []}
                onChange={(newCriteria) => updatePhaseField('readinessCriteria', newCriteria)}
                availableAssessments={availableAssessments}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handlePhaseDialogClose} color="primary">Cancel</Button>
            <Button onClick={handleSavePhase} color="primary" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Save Phase'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const ReflectionSettings = () => {
    const [formData, setFormData] = useState({
      defaultFrequency: settings?.reflectionSettings?.defaultFrequency || 'weekly',
      weeklyReflectionPrompt: settings?.reflectionSettings?.weeklyReflectionPrompt || '',
      monthlyReflectionPrompt: settings?.reflectionSettings?.monthlyReflectionPrompt || '',
    });
    const handleChange = (e) => { const { name, value } = e.target; setFormData({ ...formData, [name]: value, }); };
    const handleSubmit = (e) => { e.preventDefault(); handleUpdateSettings('reflectionSettings', formData); };
    return (
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 600, margin: 'auto' }}>
        <Typography variant="h6" gutterBottom>Reflection Settings</Typography>
        <FormControl fullWidth margin="normal"> <InputLabel id="default-reflection-frequency-label">Default Reflection Frequency</InputLabel> <Select labelId="default-reflection-frequency-label" name="defaultFrequency" value={formData.defaultFrequency} onChange={handleChange} label="Default Reflection Frequency"> <MenuItem value="after_session">After Each Session</MenuItem> <MenuItem value="daily">Daily</MenuItem> <MenuItem value="weekly">Weekly</MenuItem> <MenuItem value="monthly">Monthly</MenuItem> </Select> </FormControl>
        <TextField fullWidth margin="normal" name="weeklyReflectionPrompt" label="Weekly Reflection Prompt" value={formData.weeklyReflectionPrompt} onChange={handleChange} multiline rows={3} placeholder="Prompt for weekly reflections" />
        <TextField fullWidth margin="normal" name="monthlyReflectionPrompt" label="Monthly Reflection Prompt" value={formData.monthlyReflectionPrompt} onChange={handleChange} multiline rows={3} placeholder="Prompt for monthly reflections" />
        <Button type="submit" variant="contained" color="primary" sx={{ mt: 3 }} disabled={loading}> {loading ? <CircularProgress size={24} /> : 'Save Reflection Settings'} </Button>
      </Box>
    );
  };
  
  if (loading && !settings) { return ( <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}> <CircularProgress /> </Box> ); }
  
  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h5" component="h1" gutterBottom> Platform Therapy Settings </Typography>
      {error && ( <Alert severity="error" sx={{ mb: 2 }}> {error} </Alert> )}
      {success && ( <Alert severity="success" sx={{ mb: 2 }}> {success} </Alert> )}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="platform settings tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Session Structure" />
          <Tab label="Treatment Pathways" />
          <Tab label="Reflection Settings" />
          <Tab label="Voice Settings" />
          <Tab label="AI Response Delays" />
          <Tab label="Onboarding Configuration" />
          <Tab label="Registration" />
          <Tab label="Gen 2 Configuration" />
          <Tab label="Advanced Voice Mode" />
          <Tab label="Voice &amp; Avatar" />
          <Tab label="Client UI" />
          <Tab label="Persona Representations" />
          <Tab label="Image Generation" />
        </Tabs>
      </Box>
      <TabPanel value={currentTab} index={0}> <SessionStructureSettings /> </TabPanel>
      <TabPanel value={currentTab} index={1}> <TreatmentPathwaysManager /> </TabPanel>
      <TabPanel value={currentTab} index={2}> <ReflectionSettings /> </TabPanel>
      <TabPanel value={currentTab} index={3}>
        <VoiceSettingsTab
          handleUpdateSettings={handleUpdateSettings}
          loading={loading}
          voiceSettings={voiceSettings}
          setVoiceSettings={setVoiceSettings}
          availableVoices={availableElevenLabsVoices}
          sampleText={samplePreviewText}
          setSampleText={setSamplePreviewText}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={4}>
        <AiResponseDelaySettingsTab
          settings={settings}
          handleUpdateSettings={handleUpdateSettings}
          loading={loading}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={5}>
        <OnboardingConfigurationSettings
          settings={settings}
          handleUpdateSettings={handleUpdateSettings}
          loading={loading}
          availableFlows={availableOnboardingFlows}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={6}>
        <RegistrationSettings
          settings={settings}
          handleUpdateSettings={handleUpdateSettings}
          loading={loading}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={7}>
        <Gen2ConfigurationSettings
          settings={settings}
          handleUpdateSettings={handleUpdateSettings}
          loading={loading}
          token={token}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={8}>
        <AdvancedVoiceModeSettings
          settings={settings}
          handleUpdateSettings={handleUpdateSettings}
          loading={loading}
          token={token}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={9}>
        <VoiceAndAvatarPlatformSettings
          settings={settings}
          handleUpdateSettings={handleUpdateSettings}
          loading={loading}
          token={token}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={10}>
        <ClientUiSettings
          settings={settings}
          handleUpdateSettings={handleUpdateSettings}
          loading={loading}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={11}>
        <PersonaRepresentationManager token={token} />
      </TabPanel>
      <TabPanel value={currentTab} index={12}>
        <ImageGenerationSettingsTab
          settings={settings}
          handleUpdateSettings={handleUpdateSettings}
          loading={loading}
        />
      </TabPanel>
    </Paper>
  );
};

// Voice & Avatar platform settings — drives the client voice selection cascade.
const VoiceAndAvatarPlatformSettings = ({ settings, handleUpdateSettings, loading, token }) => {
  const va = settings?.voiceAndAvatar || {};
  const [form, setForm] = useState({
    voiceEnabled: va.voiceSelection?.enabled !== false,
    defaultAllowClientSelection: !!va.voiceSelection?.defaultAllowClientSelection,
    platformDefaultVoiceCatalogIds: va.voiceSelection?.platformDefaultVoiceCatalogIds || [],
    avatarEnabled: va.avatar?.enabled !== false,
    allowedStyles: va.avatar?.allowedStyles || ['abstract', 'illustrated'],
    defaultStyle: va.avatar?.defaultStyle || 'illustrated'
  });
  const [voices, setVoices] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        const res = await axios.get(`${API}/admin/voice-library/voices`, { headers: { 'x-auth-token': token } });
        setVoices(res.data?.voices || []);
      } catch (e) { /* non-fatal */ }
    };
    load();
  }, [token]);

  const save = async () => {
    await handleUpdateSettings('voiceAndAvatar', {
      voiceSelection: {
        enabled: form.voiceEnabled,
        defaultAllowClientSelection: form.defaultAllowClientSelection,
        platformDefaultVoiceCatalogIds: form.platformDefaultVoiceCatalogIds
      },
      avatar: {
        enabled: form.avatarEnabled,
        allowedStyles: form.allowedStyles,
        defaultStyle: form.defaultStyle
      }
    });
  };

  const toggleStyle = s => setForm(f => ({
    ...f,
    allowedStyles: f.allowedStyles.includes(s)
      ? f.allowedStyles.filter(x => x !== s)
      : [...f.allowedStyles, s]
  }));

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Voice &amp; Avatar Platform Settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Controls the client voice selection cascade (admin pool → therapist whitelist → client pick) and allowed avatar styles.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Voice Selection</Typography>
        <FormControlLabel
          control={<Switch checked={form.voiceEnabled}
            onChange={e => setForm({ ...form, voiceEnabled: e.target.checked })} />}
          label="Enable voice selection platform-wide"
        />
        <FormControlLabel
          control={<Switch checked={form.defaultAllowClientSelection}
            onChange={e => setForm({ ...form, defaultAllowClientSelection: e.target.checked })} />}
          label="Default: allow clients to pick their voice (when therapist/client have no explicit override)"
        />
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Platform-default voice pool (used during onboarding or when a therapist has no whitelist):
          </Typography>
          <FormControl fullWidth>
            <Select
              multiple
              value={form.platformDefaultVoiceCatalogIds}
              onChange={e => setForm({ ...form, platformDefaultVoiceCatalogIds: e.target.value })}
              renderValue={sel => sel.map(id => {
                const v = voices.find(v => v._id === id);
                return v ? v.name : id;
              }).join(', ')}
            >
              {voices.map(v => (
                <MenuItem key={v._id} value={v._id}>
                  {v.name} — {v.clientDescription || v.internalDescription}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Avatars</Typography>
        <FormControlLabel
          control={<Switch checked={form.avatarEnabled}
            onChange={e => setForm({ ...form, avatarEnabled: e.target.checked })} />}
          label="Show persona avatars in client UI"
        />
        <Typography variant="body2" sx={{ mt: 1 }}>Allowed styles:</Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
          {['abstract', 'illustrated', 'semi_realistic', 'photo_real'].map(s => (
            <Chip
              key={s}
              label={s}
              color={form.allowedStyles.includes(s) ? 'primary' : 'default'}
              variant={form.allowedStyles.includes(s) ? 'filled' : 'outlined'}
              onClick={() => toggleStyle(s)}
            />
          ))}
        </Box>
        <FormControl sx={{ mt: 2, minWidth: 220 }}>
          <InputLabel>Default style</InputLabel>
          <Select
            label="Default style"
            value={form.defaultStyle}
            onChange={e => setForm({ ...form, defaultStyle: e.target.value })}
          >
            {['abstract', 'illustrated', 'semi_realistic', 'photo_real'].map(s => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Button variant="contained" onClick={save} disabled={loading}>
        Save Voice &amp; Avatar Settings
      </Button>
    </Box>
  );
};

// Advanced Voice Mode Settings Tab Component
const AdvancedVoiceModeSettings = ({ settings, handleUpdateSettings, loading, token }) => {
  const voiceConfig = settings?.voiceModeConfiguration || {};
  const [formData, setFormData] = useState({
    enabled: voiceConfig.enabled || false,
    provider: voiceConfig.provider || 'gemini_multimodal_live',
    model: voiceConfig.geminiLive?.model || 'gemini-3.1-flash-live-preview',
    voicePreset: voiceConfig.geminiLive?.voicePreset || 'Sulafat',
    accentPresetId: voiceConfig.accentPresetId || null,
    accentCustomGuidance: voiceConfig.accentCustomGuidance || '',
    temperature: voiceConfig.geminiLive?.temperature || 0.7,
    maxSessionDurationSeconds: voiceConfig.geminiLive?.maxSessionDurationSeconds || 3600,
    cbcCheckEnabled: voiceConfig.safety?.cbcCheckEnabled !== false,
    consentRequired: voiceConfig.safety?.consentRequired !== false,
    // Vertex-specific overrides — used only when provider = vertex_gemini_live.
    // Empty strings are sent as null so the server falls through to env vars.
    vertexProjectId: voiceConfig.vertexGeminiLive?.projectId || '',
    vertexLocation: voiceConfig.vertexGeminiLive?.location || '',
    vertexModel: voiceConfig.vertexGeminiLive?.model || 'gemini-live-2.5-flash-native-audio',
    // VAD tuning — controls when Gemini Live considers the user "finished".
    // Defaults are therapy-friendly (longer silence tolerance, less eager interruption).
    vadStartSensitivity: voiceConfig.geminiLive?.vad?.startOfSpeechSensitivity || 'START_SENSITIVITY_HIGH',
    vadEndSensitivity: voiceConfig.geminiLive?.vad?.endOfSpeechSensitivity || 'END_SENSITIVITY_LOW',
    vadPrefixPaddingMs: voiceConfig.geminiLive?.vad?.prefixPaddingMs ?? 300,
    vadSilenceDurationMs: voiceConfig.geminiLive?.vad?.silenceDurationMs ?? 1500,
  });

  useEffect(() => {
    if (settings?.voiceModeConfiguration) {
      const vc = settings.voiceModeConfiguration;
      setFormData({
        enabled: vc.enabled || false,
        provider: vc.provider || 'gemini_multimodal_live',
        model: vc.geminiLive?.model || 'gemini-3.1-flash-live-preview',
        voicePreset: vc.geminiLive?.voicePreset || 'Sulafat',
        accentPresetId: vc.accentPresetId || null,
        accentCustomGuidance: vc.accentCustomGuidance || '',
        temperature: vc.geminiLive?.temperature || 0.7,
        maxSessionDurationSeconds: vc.geminiLive?.maxSessionDurationSeconds || 3600,
        cbcCheckEnabled: vc.safety?.cbcCheckEnabled !== false,
        consentRequired: vc.safety?.consentRequired !== false,
        vertexProjectId: vc.vertexGeminiLive?.projectId || '',
        vertexLocation: vc.vertexGeminiLive?.location || '',
        vertexModel: vc.vertexGeminiLive?.model || 'gemini-live-2.5-flash-native-audio',
        vadStartSensitivity: vc.geminiLive?.vad?.startOfSpeechSensitivity || 'START_SENSITIVITY_HIGH',
        vadEndSensitivity: vc.geminiLive?.vad?.endOfSpeechSensitivity || 'END_SENSITIVITY_LOW',
        vadPrefixPaddingMs: vc.geminiLive?.vad?.prefixPaddingMs ?? 300,
        vadSilenceDurationMs: vc.geminiLive?.vad?.silenceDurationMs ?? 1500,
      });
    }
  }, [settings]);

  const handleSave = (e) => {
    e.preventDefault();
    handleUpdateSettings('voiceModeConfiguration', {
      enabled: formData.enabled,
      provider: formData.provider,
      accentPresetId: formData.accentPresetId,
      accentCustomGuidance: formData.accentCustomGuidance || null,
      geminiLive: {
        model: formData.model,
        voicePreset: formData.voicePreset,
        temperature: formData.temperature,
        maxSessionDurationSeconds: formData.maxSessionDurationSeconds,
        vad: {
          startOfSpeechSensitivity: formData.vadStartSensitivity,
          endOfSpeechSensitivity: formData.vadEndSensitivity,
          prefixPaddingMs: formData.vadPrefixPaddingMs,
          silenceDurationMs: formData.vadSilenceDurationMs,
        },
      },
      vertexGeminiLive: {
        projectId: formData.vertexProjectId.trim() || null,
        location: formData.vertexLocation.trim() || null,
        model: formData.vertexModel.trim() || 'gemini-live-2.5-flash-native-audio',
      },
      safety: {
        requireTranscriptionBeforeResponse: true,
        cbcCheckEnabled: formData.cbcCheckEnabled,
        transcriptionStorageEnabled: true,
        consentRequired: formData.consentRequired,
      }
    });
  };

  // Voice presets now loaded from VoiceSelector component via API

  return (
    <Box component="form" onSubmit={handleSave}>
      <Typography variant="h6" gutterBottom>Advanced Voice Mode</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Controls real-time voice conversations between clients and AI therapists using the Gemini Multimodal Live API.
        Currently supports Gen1 personas only.
      </Typography>

      {/* Kill Switch */}
      <Card sx={{ mb: 3, border: formData.enabled ? '2px solid #4caf50' : '2px solid #f44336' }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                color={formData.enabled ? 'success' : 'default'}
              />
            }
            label={
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {formData.enabled ? 'Voice Mode ENABLED' : 'Voice Mode DISABLED'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Global kill switch — when disabled, no voice sessions can be started platform-wide
                </Typography>
              </Box>
            }
          />
        </CardContent>
      </Card>

      {/* Provider Settings */}
      <Accordion defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Gemini Live Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {/* Provider selector — direct Gemini API vs Vertex Gemini Live */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Provider</InputLabel>
                <Select
                  label="Provider"
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                >
                  <MenuItem value="gemini_multimodal_live">
                    Direct Gemini API (generativelanguage.googleapis.com) — not BAA-covered
                  </MenuItem>
                  <MenuItem value="vertex_gemini_live">
                    Vertex AI Gemini Live (recommended — BAA-covered, configurable safety filters)
                  </MenuItem>
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {formData.provider === 'vertex_gemini_live'
                    ? 'Routes through Vertex AI in your GCP project. Auth via Application Default Credentials. Currently GA model is gemini-live-2.5-flash-native-audio.'
                    : 'Direct Gemini API uses GEMINI_API_KEY. Latest preview models available here first; not under HIPAA BAA.'}
                </Typography>
              </FormControl>
            </Grid>

            {/* Direct API model (always editable; ignored when provider=vertex) */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Direct Gemini API model"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                size="small"
                disabled={formData.provider !== 'gemini_multimodal_live'}
                helperText={
                  formData.provider === 'gemini_multimodal_live'
                    ? 'e.g. gemini-3.1-flash-live-preview'
                    : 'Used only when Provider = Direct Gemini API'
                }
              />
            </Grid>

            {/* Vertex-specific config — only when Vertex provider selected */}
            {formData.provider === 'vertex_gemini_live' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Vertex Live model"
                    value={formData.vertexModel}
                    onChange={(e) => setFormData({ ...formData, vertexModel: e.target.value })}
                    size="small"
                    helperText="GA: gemini-live-2.5-flash-native-audio. Update here when 3.x reaches GA on Vertex."
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Vertex GCP project ID"
                    value={formData.vertexProjectId}
                    onChange={(e) => setFormData({ ...formData, vertexProjectId: e.target.value })}
                    size="small"
                    placeholder="(falls back to VERTEX_AI_PROJECT_ID env)"
                    helperText="Leave blank to inherit from server env vars"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Vertex location"
                    value={formData.vertexLocation}
                    onChange={(e) => setFormData({ ...formData, vertexLocation: e.target.value })}
                    size="small"
                    placeholder="e.g. europe-west1"
                    helperText="Region for Vertex endpoint. Default: europe-west1"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Accent / Regional Style</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Select an accent preset or provide custom guidance. Previews approximate the accent using TTS; live sessions use AI accent steering.
              </Typography>
              <AccentSelector
                value={formData.accentPresetId}
                onChange={(presetId) => setFormData({ ...formData, accentPresetId: presetId })}
                customGuidance={formData.accentCustomGuidance}
                onCustomGuidanceChange={(text) => setFormData({ ...formData, accentCustomGuidance: text })}
                token={token}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Platform Default Voice</Typography>
              <VoiceSelector
                value={formData.voicePreset}
                onChange={(voice) => setFormData({ ...formData, voicePreset: voice || 'Sulafat' })}
                token={token}
                accentPresetId={formData.accentPresetId}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="body2" gutterBottom>Temperature: {formData.temperature}</Typography>
                <Slider
                  value={formData.temperature}
                  onChange={(e, val) => setFormData({ ...formData, temperature: val })}
                  min={0}
                  max={2}
                  step={0.1}
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Session Duration (seconds)"
                type="number"
                value={formData.maxSessionDurationSeconds}
                onChange={(e) => setFormData({ ...formData, maxSessionDurationSeconds: parseInt(e.target.value) || 3600 })}
                size="small"
                helperText="Maximum voice session length before auto-disconnect"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Voice Activity Detection */}
      <Accordion defaultExpanded={false} sx={{ mt: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Voice Activity Detection (VAD)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Controls when Gemini Live decides the client has finished speaking. Defaults are tuned
            for therapy: tolerant of reflective pauses (1.5s silence), less eager to interrupt mid-thought.
            If clients report being cut off, increase silence duration. If the AI hangs without responding,
            increase start-of-speech sensitivity or decrease silence duration.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>End-of-speech sensitivity</InputLabel>
                <Select
                  label="End-of-speech sensitivity"
                  value={formData.vadEndSensitivity}
                  onChange={(e) => setFormData({ ...formData, vadEndSensitivity: e.target.value })}
                >
                  <MenuItem value="END_SENSITIVITY_LOW">LOW (tolerant of pauses — therapy default)</MenuItem>
                  <MenuItem value="END_SENSITIVITY_HIGH">HIGH (eager — fast casual chat)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Start-of-speech sensitivity</InputLabel>
                <Select
                  label="Start-of-speech sensitivity"
                  value={formData.vadStartSensitivity}
                  onChange={(e) => setFormData({ ...formData, vadStartSensitivity: e.target.value })}
                >
                  <MenuItem value="START_SENSITIVITY_HIGH">HIGH (responsive — therapy default)</MenuItem>
                  <MenuItem value="START_SENSITIVITY_LOW">LOW (ignores brief sounds)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Silence duration before end-of-turn (ms)"
                value={formData.vadSilenceDurationMs}
                onChange={(e) => setFormData({ ...formData, vadSilenceDurationMs: parseInt(e.target.value) || 1500 })}
                size="small"
                inputProps={{ min: 200, max: 5000, step: 100 }}
                helperText="200–5000. Higher = more patient. Default 1500ms."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Prefix padding (ms)"
                value={formData.vadPrefixPaddingMs}
                onChange={(e) => setFormData({ ...formData, vadPrefixPaddingMs: parseInt(e.target.value) || 300 })}
                size="small"
                inputProps={{ min: 0, max: 2000, step: 50 }}
                helperText="Audio captured before VAD detects speech. Avoids cutting off start of words."
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Safety Settings */}
      <Accordion defaultExpanded={false} sx={{ mt: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">Safety Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Switch
                checked={formData.cbcCheckEnabled}
                onChange={(e) => setFormData({ ...formData, cbcCheckEnabled: e.target.checked })}
              />
            }
            label="CBC safety check on voice input (recommended: always on)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.consentRequired}
                onChange={(e) => setFormData({ ...formData, consentRequired: e.target.checked })}
              />
            }
            label="Require user consent before first voice session"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Transcription storage is always enabled — all voice interactions are saved as text messages for audit compliance.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Button type="submit" variant="contained" color="primary" sx={{ mt: 3 }} disabled={loading}>
        {loading ? <CircularProgress size={24} /> : 'Save Voice Mode Settings'}
      </Button>
    </Box>
  );
};

// Voice Settings Tab Component
const VoiceSettingsTab = ({ 
  handleUpdateSettings, 
  loading, 
  voiceSettings,
  setVoiceSettings,
  availableVoices,
  sampleText,
  setSampleText
}) => {
  const handleVoiceChange = (voiceType, value) => {
    setVoiceSettings(prev => ({
      ...prev,
      [voiceType]: value
    }));
  };

  const handleSubmit = (e) => { 
    e.preventDefault(); 
    handleUpdateSettings('voiceSettings', voiceSettings); 
  };

  const voiceTypes = [
    { key: 'defaultAITherapistVoiceId', label: 'AI Therapist Voice', helperText: 'Voice used for AI therapist responses' },
    { key: 'defaultClientVoiceId', label: 'Client Voice', helperText: 'Default voice for client text-to-speech' },
    { key: 'defaultSystemVoiceId', label: 'System Voice', helperText: 'Voice for system messages and notifications' },
    { key: 'defaultHumanTherapistVoiceId', label: 'Human Therapist Voice', helperText: 'Default voice for human therapist messages' },
    { key: 'defaultSupervisorVoiceId', label: 'Supervisor Voice', helperText: 'Voice used in supervisor consultations' }
  ];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 800, margin: 'auto' }}>
      <Typography variant="h6" gutterBottom>Voice Configuration</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure default voices for different roles in the platform. These can be overridden at the user level.
      </Typography>
      
      <Grid container spacing={3}>
        {voiceTypes.map(({ key, label, helperText }) => (
          <Grid item xs={12} key={key}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>{label}</Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel id={`${key}-select-label`}>Select {label}</InputLabel>
                <Select 
                  labelId={`${key}-select-label`} 
                  value={voiceSettings?.[key] || ''} 
                  onChange={(e) => handleVoiceChange(key, e.target.value)} 
                  label={`Select ${label}`}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {availableVoices.map((voice) => (
                    <MenuItem key={voice.voice_id} value={voice.voice_id}>
                      {voice.name} ({voice.labels?.gender || 'N/A'}, {voice.labels?.accent || 'N/A'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField 
                fullWidth 
                margin="normal" 
                label={`${label} ID (Manual Entry)`}
                value={voiceSettings?.[key] || ''} 
                onChange={(e) => handleVoiceChange(key, e.target.value)} 
                helperText={helperText}
              />
              {voiceSettings?.[key] && sampleText && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>Preview:</Typography>
                  <TextToSpeech 
                    text={sampleText} 
                    voiceId={voiceSettings[key]} 
                    showVoiceSelector={false} 
                    useStreaming={false} 
                    size="small" 
                  />
                </Box>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
      
      <Card variant="outlined" sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Voice Preview</Typography>
        <TextField 
          fullWidth 
          margin="normal" 
          label="Sample Text for Preview" 
          value={sampleText} 
          onChange={(e) => setSampleText(e.target.value)} 
          multiline 
          rows={2}
          placeholder="Type text here to preview how it sounds with each voice"
        />
      </Card>
      
      <Button type="submit" variant="contained" color="primary" sx={{ mt: 3 }} disabled={loading}>
        {loading ? <CircularProgress size={24} /> : 'Save Voice Settings'}
      </Button>
    </Box>
  );
};

// AI Response Delay Settings Tab Component
const AiResponseDelaySettingsTab = ({ settings, handleUpdateSettings, loading }) => {
  const [formData, setFormData] = useState({
    defaultThinkingDelayMs: settings?.aiResponseDelaySettings?.defaultThinkingDelayMs || 1000,
    defaultTypingDelayMs: settings?.aiResponseDelaySettings?.defaultTypingDelayMs || 2000,
    enableRandomization: settings?.aiResponseDelaySettings?.enableRandomization || false,
    thinkingDelayMaxMs: settings?.aiResponseDelaySettings?.thinkingDelayMaxMs || 1500,
    typingDelayMaxMs: settings?.aiResponseDelaySettings?.typingDelayMaxMs || 3000,
  });

  useEffect(() => {
    if (settings && settings.aiResponseDelaySettings) {
      setFormData({
        defaultThinkingDelayMs: settings.aiResponseDelaySettings.defaultThinkingDelayMs || 1000,
        defaultTypingDelayMs: settings.aiResponseDelaySettings.defaultTypingDelayMs || 2000,
        enableRandomization: settings.aiResponseDelaySettings.enableRandomization || false,
        thinkingDelayMaxMs: settings.aiResponseDelaySettings.thinkingDelayMaxMs || 1500,
        typingDelayMaxMs: settings.aiResponseDelaySettings.typingDelayMaxMs || 3000,
      });
    } else {
      setFormData({
        defaultThinkingDelayMs: 1000,
        defaultTypingDelayMs: 2000,
        enableRandomization: false,
        thinkingDelayMaxMs: 1500,
        typingDelayMaxMs: 3000,
      });
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value, 10) || 0 : value),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.enableRandomization) {
      if (formData.thinkingDelayMaxMs < formData.defaultThinkingDelayMs) {
        alert("Max thinking delay must be greater than or equal to default thinking delay.");
        return;
      }
      if (formData.typingDelayMaxMs < formData.defaultTypingDelayMs) {
        alert("Max typing delay must be greater than or equal to default typing delay.");
        return;
      }
    }
    handleUpdateSettings('aiResponseDelaySettings', formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 700, margin: 'auto' }}>
      <Typography variant="h6" gutterBottom>AI Response Delay Configuration</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth type="number" label="Default Thinking Delay (ms)" name="defaultThinkingDelayMs" value={formData.defaultThinkingDelayMs} onChange={handleChange} helperText="Time AI 'thinks' before showing 'typing'." InputProps={{ inputProps: { min: 0 } }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth type="number" label="Default Typing Delay (ms)" name="defaultTypingDelayMs" value={formData.defaultTypingDelayMs} onChange={handleChange} helperText="Duration 'typing' indicator is shown." InputProps={{ inputProps: { min: 0 } }} />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel control={ <Switch checked={formData.enableRandomization} onChange={handleChange} name="enableRandomization" /> } label="Enable Delay Randomization" />
        </Grid>
        {formData.enableRandomization && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Max Thinking Delay (ms)" name="thinkingDelayMaxMs" value={formData.thinkingDelayMaxMs} onChange={handleChange} helperText="Upper bound for randomized thinking delay." InputProps={{ inputProps: { min: formData.defaultThinkingDelayMs } }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label="Max Typing Delay (ms)" name="typingDelayMaxMs" value={formData.typingDelayMaxMs} onChange={handleChange} helperText="Upper bound for randomized typing delay." InputProps={{ inputProps: { min: formData.defaultTypingDelayMs } }} />
            </Grid>
          </>
        )}
      </Grid>
      <Button type="submit" variant="contained" color="primary" sx={{ mt: 3 }} disabled={loading}>
        {loading ? <CircularProgress size={24} /> : 'Save Delay Settings'}
      </Button>
    </Box>
  );
};

const OnboardingConfigurationSettings = ({ settings, handleUpdateSettings, loading, availableFlows }) => {
  const [formData, setFormData] = useState({
    useConfigurableOnboardingFlows: settings?.onboardingConfiguration?.useConfigurableOnboardingFlows ?? true,
    defaultOnboardingFlowId: settings?.onboardingConfiguration?.defaultOnboardingFlowId || '',
    allowServiceGroupOverride: settings?.onboardingConfiguration?.allowServiceGroupOverride ?? true,
    allowInvitationOverride: settings?.onboardingConfiguration?.allowInvitationOverride ?? true,
    requireFlowCompletion: settings?.onboardingConfiguration?.requireFlowCompletion ?? true,
    maxOnboardingDurationDays: settings?.onboardingConfiguration?.maxOnboardingDurationDays || 30,
    useUnifiedLLMPipeline: settings?.onboardingConfiguration?.useUnifiedLLMPipeline ?? false,
    useUnifiedAssessmentCompletion: settings?.onboardingConfiguration?.useUnifiedAssessmentCompletion ?? false,
    useUnifiedMessageEndpoint: settings?.onboardingConfiguration?.useUnifiedMessageEndpoint ?? false,
  });

  useEffect(() => {
    if (settings?.onboardingConfiguration) {
      setFormData({
        useConfigurableOnboardingFlows: settings.onboardingConfiguration.useConfigurableOnboardingFlows ?? true,
        defaultOnboardingFlowId: settings.onboardingConfiguration.defaultOnboardingFlowId || '',
        allowServiceGroupOverride: settings.onboardingConfiguration.allowServiceGroupOverride ?? true,
        allowInvitationOverride: settings.onboardingConfiguration.allowInvitationOverride ?? true,
        requireFlowCompletion: settings.onboardingConfiguration.requireFlowCompletion ?? true,
        maxOnboardingDurationDays: settings.onboardingConfiguration.maxOnboardingDurationDays || 30,
        useUnifiedLLMPipeline: settings.onboardingConfiguration.useUnifiedLLMPipeline ?? false,
        useUnifiedAssessmentCompletion: settings.onboardingConfiguration.useUnifiedAssessmentCompletion ?? false,
        useUnifiedMessageEndpoint: settings.onboardingConfiguration.useUnifiedMessageEndpoint ?? false,
      });
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Convert empty string to null for defaultOnboardingFlowId
    const dataToSubmit = {
      ...formData,
      defaultOnboardingFlowId: formData.defaultOnboardingFlowId || null
    };
    handleUpdateSettings('onboardingConfiguration', dataToSubmit);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 700, margin: 'auto' }}>
      <Typography variant="h6" gutterBottom>Onboarding Configuration</Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Control how new users complete their onboarding process. Configurable flows allow multi-stage
        onboarding with custom AI personas and assessments.
      </Alert>

      <FormControlLabel
        control={
          <Switch
            checked={formData.useConfigurableOnboardingFlows}
            onChange={handleChange}
            name="useConfigurableOnboardingFlows"
          />
        }
        label="Use Configurable Onboarding Flows"
        sx={{ mb: 2, display: 'block' }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 4 }}>
        When enabled, uses multi-stage onboarding flows. When disabled, falls back to legacy single-stage onboarding.
      </Typography>

      {formData.useConfigurableOnboardingFlows && (
        <>
          <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
            <InputLabel id="default-flow-label">Default Onboarding Flow</InputLabel>
            <Select
              labelId="default-flow-label"
              name="defaultOnboardingFlowId"
              value={formData.defaultOnboardingFlowId}
              onChange={handleChange}
              label="Default Onboarding Flow"
            >
              <MenuItem value="">
                <em>-- No Default (Use Legacy) --</em>
              </MenuItem>
              {availableFlows.map(flow => (
                <MenuItem key={flow._id} value={flow._id}>
                  {flow.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 1 }}>
            Platform-wide default flow. Can be overridden at service group or invitation level.
            If not set, legacy onboarding is used.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom>Override Permissions</Typography>

          <FormControlLabel
            control={
              <Switch
                checked={formData.allowServiceGroupOverride}
                onChange={handleChange}
                name="allowServiceGroupOverride"
              />
            }
            label="Allow service groups to override default flow"
            sx={{ mb: 1, display: 'block' }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.allowInvitationOverride}
                onChange={handleChange}
                name="allowInvitationOverride"
              />
            }
            label="Allow invitations to override default flow"
            sx={{ mb: 3, display: 'block' }}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom>Completion Requirements</Typography>

          <FormControlLabel
            control={
              <Switch
                checked={formData.requireFlowCompletion}
                onChange={handleChange}
                name="requireFlowCompletion"
              />
            }
            label="Require all flow stages to be completed"
            sx={{ mb: 2, display: 'block' }}
          />

          <TextField
            fullWidth
            type="number"
            label="Maximum Onboarding Duration (days)"
            name="maxOnboardingDurationDays"
            value={formData.maxOnboardingDurationDays}
            onChange={handleChange}
            helperText="Maximum days to complete onboarding before expiry"
            InputProps={{ inputProps: { min: 1, max: 90 } }}
            sx={{ mb: 2 }}
          />
        </>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
        Unified Message Pipeline
      </Typography>

      <Alert severity="warning" sx={{ mb: 2 }}>
        These flags control the migration from dual onboarding/therapy code paths to a single unified pipeline.
        Enable incrementally and test each step. Set to false to instantly rollback.
      </Alert>

      <FormControlLabel
        control={
          <Switch
            checked={formData.useUnifiedLLMPipeline}
            onChange={handleChange}
            name="useUnifiedLLMPipeline"
          />
        }
        label="Phase 1-2: Unified LLM Pipeline"
        sx={{ mb: 1, display: 'block' }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 4 }}>
        Routes onboarding LLM calls through the full generateResponse() pipeline with logging, safety features, and socket emission.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={formData.useUnifiedAssessmentCompletion}
            onChange={handleChange}
            name="useUnifiedAssessmentCompletion"
          />
        }
        label="Phase 3: Unified Assessment Completion"
        sx={{ mb: 1, display: 'block' }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 4 }}>
        Assessment completion uses a single handler for both onboarding and therapy (eliminates the dual-path branching).
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={formData.useUnifiedMessageEndpoint}
            onChange={handleChange}
            name="useUnifiedMessageEndpoint"
          />
        }
        label="Phase 4: Unified Message Endpoint"
        sx={{ mb: 1, display: 'block' }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 4 }}>
        All onboarding messages route through the therapy chat controller. Full safety features (CBC, parasocial, looping) apply to Stage 2+ onboarding.
      </Typography>

      <Button type="submit" variant="contained" color="primary" sx={{ mt: 3 }} disabled={loading}>
        {loading ? <CircularProgress size={24} /> : 'Save Onboarding Configuration'}
      </Button>
    </Box>
  );
};

// Registration Settings Component
const RegistrationSettings = ({ settings, handleUpdateSettings, loading }) => {
  const [formData, setFormData] = useState({
    allowPublicSignup: settings?.registrationSettings?.allowPublicSignup ?? true,
  });

  useEffect(() => {
    if (settings?.registrationSettings) {
      setFormData({
        allowPublicSignup: settings.registrationSettings.allowPublicSignup ?? true,
      });
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleUpdateSettings('registrationSettings', formData);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 700, margin: 'auto' }}>
      <Typography variant="h6" gutterBottom>Registration Settings</Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Control how users can register for the platform. These settings do not affect users
        who have received invitations or general signup links.
      </Alert>

      <FormControlLabel
        control={
          <Switch
            checked={formData.allowPublicSignup}
            onChange={handleChange}
            name="allowPublicSignup"
          />
        }
        label="Allow Public Signup"
        sx={{ mb: 2, display: 'block' }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 4 }}>
        When enabled, users can sign up without an invitation by clicking "Don't have an account? Sign Up"
        on the login page. When disabled, the signup option is hidden and users can only register via
        invitation links or general signup codes.
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>Note:</strong> Disabling public signup will NOT prevent users with valid invitation links
        or general signup codes from registering. It only hides the general "Sign Up" option on the login page.
      </Alert>

      <Button type="submit" variant="contained" color="primary" sx={{ mt: 3 }} disabled={loading}>
        {loading ? <CircularProgress size={24} /> : 'Save Registration Settings'}
      </Button>
    </Box>
  );
};

// Readiness Criteria Editor Component
const ReadinessCriteriaEditor = ({ criteria, onChange, availableAssessments }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingCriterion, setEditingCriterion] = useState(null);

  const getDefaultCriterion = () => ({
    id: `criterion_${Date.now()}`,
    description: '',
    evaluationType: 'manual',
    dataSource: 'custom',
    assessmentId: '',
    fieldPath: '',
    operator: '>=',
    threshold: null,
    weight: 1
  });

  const handleAddCriterion = () => {
    setEditingCriterion(getDefaultCriterion());
    setEditingIndex(-1); // -1 means new
  };

  const handleEditCriterion = (index) => {
    setEditingCriterion({ ...criteria[index] });
    setEditingIndex(index);
  };

  const handleSaveCriterion = () => {
    if (!editingCriterion.description) {
      alert('Description is required');
      return;
    }

    const newCriteria = [...criteria];
    if (editingIndex === -1) {
      newCriteria.push(editingCriterion);
    } else {
      newCriteria[editingIndex] = editingCriterion;
    }
    onChange(newCriteria);
    setEditingCriterion(null);
    setEditingIndex(null);
  };

  const handleDeleteCriterion = (index) => {
    if (window.confirm('Are you sure you want to delete this criterion?')) {
      const newCriteria = criteria.filter((_, i) => i !== index);
      onChange(newCriteria);
    }
  };

  const handleCancelEdit = () => {
    setEditingCriterion(null);
    setEditingIndex(null);
  };

  const updateCriterion = (field, value) => {
    setEditingCriterion(prev => ({ ...prev, [field]: value }));
  };

  const getEvaluationTypeLabel = (type) => {
    switch (type) {
      case 'auto': return 'Automatic';
      case 'manual': return 'Manual Attestation';
      case 'hybrid': return 'Hybrid (Auto + Manual Override)';
      default: return type;
    }
  };

  const getDataSourceLabel = (source) => {
    switch (source) {
      case 'assessment': return 'Assessment Score';
      case 'check_in': return 'Check-In Metric';
      case 'session_count': return 'Session Count';
      case 'goal_completion': return 'Goal Completion Rate';
      case 'custom': return 'Manual/Custom';
      default: return source;
    }
  };

  const operatorOptions = [
    { value: '<', label: '< (less than)' },
    { value: '<=', label: '<= (less than or equal)' },
    { value: '=', label: '= (equal to)' },
    { value: '>=', label: '>= (greater than or equal)' },
    { value: '>', label: '> (greater than)' },
    { value: 'exists', label: 'exists (has value)' },
    { value: 'not_exists', label: 'not exists (no value)' }
  ];

  // Calculate overall readiness if all criteria were met
  const totalWeight = criteria.reduce((sum, c) => sum + (c.weight || 1), 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="subtitle2">Readiness Criteria</Typography>
          <Typography variant="body2" color="text.secondary">
            Define criteria that must be met before advancing to the next phase.
            Total weight: {totalWeight} (each criterion contributes its weight to the readiness score)
          </Typography>
        </Box>
        <Button startIcon={<AddIcon />} onClick={handleAddCriterion} variant="outlined" size="small">
          Add Criterion
        </Button>
      </Box>

      {criteria.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No readiness criteria defined. Clients will be considered automatically ready to advance.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {criteria.map((criterion, index) => (
            <Card key={criterion.id || index} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2">{criterion.description}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={getEvaluationTypeLabel(criterion.evaluationType)}
                        size="small"
                        color={criterion.evaluationType === 'auto' ? 'success' : criterion.evaluationType === 'manual' ? 'warning' : 'info'}
                        variant="outlined"
                      />
                      {criterion.evaluationType !== 'manual' && (
                        <Chip
                          label={getDataSourceLabel(criterion.dataSource)}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {criterion.threshold !== null && criterion.threshold !== undefined && (
                        <Chip
                          label={`${criterion.operator} ${criterion.threshold}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      <Chip
                        label={`Weight: ${criterion.weight || 1}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                    {criterion.assessmentId && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Assessment: {criterion.assessmentId} &rarr; {criterion.fieldPath || 'totalScore'}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => handleEditCriterion(index)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteCriterion(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Edit/Add Criterion Dialog */}
      <Dialog open={editingCriterion !== null} onClose={handleCancelEdit} maxWidth="sm" fullWidth>
        <DialogTitle>{editingIndex === -1 ? 'Add Criterion' : 'Edit Criterion'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={editingCriterion?.description || ''}
            onChange={(e) => updateCriterion('description', e.target.value)}
            required
            multiline
            rows={2}
            placeholder="e.g., Significant reduction in PTSD symptoms (PCL-5 < 33)"
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="eval-type-label">Evaluation Type</InputLabel>
            <Select
              labelId="eval-type-label"
              value={editingCriterion?.evaluationType || 'manual'}
              onChange={(e) => updateCriterion('evaluationType', e.target.value)}
              label="Evaluation Type"
            >
              <MenuItem value="auto">Automatic - System evaluates based on data</MenuItem>
              <MenuItem value="manual">Manual - Therapist must attest</MenuItem>
              <MenuItem value="hybrid">Hybrid - Auto-evaluate with manual override</MenuItem>
            </Select>
          </FormControl>

          {editingCriterion?.evaluationType !== 'manual' && (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel id="data-source-label">Data Source</InputLabel>
                <Select
                  labelId="data-source-label"
                  value={editingCriterion?.dataSource || 'custom'}
                  onChange={(e) => updateCriterion('dataSource', e.target.value)}
                  label="Data Source"
                >
                  <MenuItem value="assessment">Assessment Score</MenuItem>
                  <MenuItem value="check_in">Check-In Metric</MenuItem>
                  <MenuItem value="session_count">Session Count</MenuItem>
                  <MenuItem value="goal_completion">Goal Completion Rate</MenuItem>
                  <MenuItem value="custom">Custom/Other</MenuItem>
                </Select>
              </FormControl>

              {editingCriterion?.dataSource === 'assessment' && (
                <>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="assessment-id-label">Assessment</InputLabel>
                    <Select
                      labelId="assessment-id-label"
                      value={editingCriterion?.assessmentId || ''}
                      onChange={(e) => updateCriterion('assessmentId', e.target.value)}
                      label="Assessment"
                    >
                      <MenuItem value=""><em>Select an assessment</em></MenuItem>
                      {availableAssessments.map(a => (
                        <MenuItem key={a.testId || a.id} value={a.testId || a.id}>
                          {a.name || a.testId || a.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Field Path"
                    value={editingCriterion?.fieldPath || ''}
                    onChange={(e) => updateCriterion('fieldPath', e.target.value)}
                    helperText="e.g., totalScore, subscales.avoidance"
                    placeholder="totalScore"
                  />
                </>
              )}

              {editingCriterion?.dataSource === 'check_in' && (
                <TextField
                  fullWidth
                  margin="normal"
                  label="Field Path"
                  value={editingCriterion?.fieldPath || ''}
                  onChange={(e) => updateCriterion('fieldPath', e.target.value)}
                  helperText="e.g., sudsRating, moodRating"
                  placeholder="sudsRating"
                />
              )}

              {editingCriterion?.dataSource !== 'custom' && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel id="operator-label">Operator</InputLabel>
                      <Select
                        labelId="operator-label"
                        value={editingCriterion?.operator || '>='}
                        onChange={(e) => updateCriterion('operator', e.target.value)}
                        label="Operator"
                      >
                        {operatorOptions.map(op => (
                          <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    {!['exists', 'not_exists'].includes(editingCriterion?.operator) && (
                      <TextField
                        fullWidth
                        margin="normal"
                        type="number"
                        label="Threshold"
                        value={editingCriterion?.threshold ?? ''}
                        onChange={(e) => updateCriterion('threshold', e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="e.g., 33"
                      />
                    )}
                  </Grid>
                </Grid>
              )}
            </>
          )}

          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Weight"
            value={editingCriterion?.weight || 1}
            onChange={(e) => updateCriterion('weight', parseInt(e.target.value, 10) || 1)}
            InputProps={{ inputProps: { min: 1, max: 10 } }}
            helperText="Higher weight = more importance in overall readiness calculation (1-10)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button onClick={handleSaveCriterion} variant="contained">
            {editingIndex === -1 ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Gen 2 Transdiagnostic Caregiver Configuration Settings
const Gen2ConfigurationSettings = ({ settings, handleUpdateSettings, loading, token }) => {
  const gen2Config = settings?.gen2Configuration || {};
  const [localConfig, setLocalConfig] = useState({
    enabled: gen2Config.enabled || false,
    globalModules: gen2Config.globalModules || [],
    deepThinkingTimeoutMs: gen2Config.deepThinkingTimeoutMs || 30000,
    consultationTimeoutMs: gen2Config.consultationTimeoutMs || 20000,
    statusMessages: {
      thinking: gen2Config.statusMessages?.thinking || 'Let me think about this more carefully...',
      consulting: gen2Config.statusMessages?.consulting || 'Consulting with specialist colleagues...',
      formulating: gen2Config.statusMessages?.formulating || 'Updating my understanding of your situation...'
    },
    defaultReasoningConfig: {
      deepThinkingTriggers: {
        firstSession: gen2Config.defaultReasoningConfig?.deepThinkingTriggers?.firstSession ?? true,
        significantGapDays: gen2Config.defaultReasoningConfig?.deepThinkingTriggers?.significantGapDays || 7,
        newDomainConcern: gen2Config.defaultReasoningConfig?.deepThinkingTriggers?.newDomainConcern ?? true,
        measurementChange: gen2Config.defaultReasoningConfig?.deepThinkingTriggers?.measurementChange ?? true,
        clinicalDecisionPoint: gen2Config.defaultReasoningConfig?.deepThinkingTriggers?.clinicalDecisionPoint ?? true
      },
      consultationTriggers: {
        periodicReviewMessages: gen2Config.defaultReasoningConfig?.consultationTriggers?.periodicReviewMessages || 10,
        domainQuestionDetected: gen2Config.defaultReasoningConfig?.consultationTriggers?.domainQuestionDetected ?? true,
        formulationChange: gen2Config.defaultReasoningConfig?.consultationTriggers?.formulationChange ?? true,
        presentationShift: gen2Config.defaultReasoningConfig?.consultationTriggers?.presentationShift ?? true
      },
      formulationContextMaxTokens: gen2Config.defaultReasoningConfig?.formulationContextMaxTokens || 500
    },
    reasoningLlmProvider: {
      provider: gen2Config.reasoningLlmProvider?.provider || 'default',
      model: gen2Config.reasoningLlmProvider?.model || '',
      temperature: gen2Config.reasoningLlmProvider?.temperature ?? 0.3
    }
  });
  const [availableModules, setAvailableModules] = useState([]);
  const [newModuleKey, setNewModuleKey] = useState('');
  const [llmConfig, setLlmConfig] = useState(null);

  // Sync local state when settings change
  useEffect(() => {
    if (settings?.gen2Configuration) {
      const gc = settings.gen2Configuration;
      setLocalConfig(prev => ({
        ...prev,
        enabled: gc.enabled || false,
        globalModules: gc.globalModules || [],
        deepThinkingTimeoutMs: gc.deepThinkingTimeoutMs || 30000,
        consultationTimeoutMs: gc.consultationTimeoutMs || 20000,
        statusMessages: {
          thinking: gc.statusMessages?.thinking || prev.statusMessages.thinking,
          consulting: gc.statusMessages?.consulting || prev.statusMessages.consulting,
          formulating: gc.statusMessages?.formulating || prev.statusMessages.formulating
        }
      }));
    }
  }, [settings?.gen2Configuration]);

  // Fetch available specialist modules
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/admin/specialist-modules?active=true`, {
          headers: { 'x-auth-token': token }
        });
        setAvailableModules(res.data || []);
      } catch (err) {
        console.error('Error fetching specialist modules:', err);
      }
    };
    if (token) fetchModules();
  }, [token]);

  // Fetch LLM configuration for provider/model dropdowns
  useEffect(() => {
    const fetchLlmConfig = async () => {
      try {
        const data = await apiService.getLLMConfiguration(token);
        setLlmConfig(data);
      } catch (err) {
        console.error('Error fetching LLM configuration:', err);
      }
    };
    if (token) fetchLlmConfig();
  }, [token]);

  const handleSave = () => {
    handleUpdateSettings('gen2Configuration', {
      ...localConfig,
      reasoningLlmProvider: {
        ...localConfig.reasoningLlmProvider,
        model: localConfig.reasoningLlmProvider.model || null
      }
    });
  };

  const updateField = (path, value) => {
    setLocalConfig(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const addGlobalModule = () => {
    if (newModuleKey && !localConfig.globalModules.includes(newModuleKey)) {
      updateField('globalModules', [...localConfig.globalModules, newModuleKey]);
      setNewModuleKey('');
    }
  };

  const removeGlobalModule = (key) => {
    updateField('globalModules', localConfig.globalModules.filter(k => k !== key));
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Gen 2 Transdiagnostic Caregiver</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure the Gen 2 transdiagnostic reasoning pipeline. When enabled, personas with gen2Config.enabled
        will use deep thinking and specialist consultation instead of direct LLM calls.
      </Typography>

      {/* Global Enable */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={localConfig.enabled}
              onChange={(e) => updateField('enabled', e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Enable Gen 2 Pipeline
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Global kill switch. When disabled, all Gen 2 personas fall back to Gen 1 processing.
              </Typography>
            </Box>
          }
        />
      </Paper>

      {localConfig.enabled && (
        <>
          {/* Timeouts */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Timeout Configuration</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" gutterBottom>
                  Deep Thinking Timeout: {(localConfig.deepThinkingTimeoutMs / 1000).toFixed(0)}s
                </Typography>
                <Slider
                  value={localConfig.deepThinkingTimeoutMs}
                  onChange={(e, v) => updateField('deepThinkingTimeoutMs', v)}
                  min={5000} max={120000} step={5000}
                  marks={[
                    { value: 5000, label: '5s' },
                    { value: 30000, label: '30s' },
                    { value: 60000, label: '60s' },
                    { value: 120000, label: '120s' }
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" gutterBottom>
                  Consultation Timeout: {(localConfig.consultationTimeoutMs / 1000).toFixed(0)}s
                </Typography>
                <Slider
                  value={localConfig.consultationTimeoutMs}
                  onChange={(e, v) => updateField('consultationTimeoutMs', v)}
                  min={5000} max={60000} step={5000}
                  marks={[
                    { value: 5000, label: '5s' },
                    { value: 20000, label: '20s' },
                    { value: 40000, label: '40s' },
                    { value: 60000, label: '60s' }
                  ]}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Status Messages */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Client-Facing Status Messages</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Messages shown to clients during Gen 2 processing phases.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Thinking Message" size="small"
                  value={localConfig.statusMessages.thinking}
                  onChange={(e) => updateField('statusMessages.thinking', e.target.value)}
                  helperText="Shown during deep thinking mode"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Consulting Message" size="small"
                  value={localConfig.statusMessages.consulting}
                  onChange={(e) => updateField('statusMessages.consulting', e.target.value)}
                  helperText="Shown during specialist consultation"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Formulating Message" size="small"
                  value={localConfig.statusMessages.formulating}
                  onChange={(e) => updateField('statusMessages.formulating', e.target.value)}
                  helperText="Shown during formulation update"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Global Modules */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Global Specialist Modules</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These modules are available to ALL Gen 2 personas, in addition to their persona-specific modules.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {localConfig.globalModules.map(key => (
                <Chip
                  key={key}
                  label={key}
                  onDelete={() => removeGlobalModule(key)}
                  color="primary" variant="outlined"
                />
              ))}
              {localConfig.globalModules.length === 0 && (
                <Typography variant="body2" color="text.secondary">No global modules configured</Typography>
              )}
            </Box>
            {availableModules.length === 0 ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                No specialist modules have been created yet. Go to <strong>Specialist Modules</strong> in the admin navigation to create modules first, then return here to assign them globally.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Add Module</InputLabel>
                  <Select
                    value={newModuleKey}
                    onChange={(e) => setNewModuleKey(e.target.value)}
                    label="Add Module"
                  >
                    {availableModules
                      .filter(m => !localConfig.globalModules.includes(m.key))
                      .map(m => (
                        <MenuItem key={m.key} value={m.key}>{m.name} ({m.key})</MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" size="small" onClick={addGlobalModule} disabled={!newModuleKey}>
                  Add
                </Button>
              </Box>
            )}
          </Paper>

          {/* Default Deep Thinking Triggers */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Default Deep Thinking Triggers</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These defaults apply when a persona does not specify its own triggers.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={localConfig.defaultReasoningConfig.deepThinkingTriggers.firstSession}
                    onChange={(e) => updateField('defaultReasoningConfig.deepThinkingTriggers.firstSession', e.target.checked)} />}
                  label="First session (no formulation exists)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={localConfig.defaultReasoningConfig.deepThinkingTriggers.newDomainConcern}
                    onChange={(e) => updateField('defaultReasoningConfig.deepThinkingTriggers.newDomainConcern', e.target.checked)} />}
                  label="New domain concern detected"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={localConfig.defaultReasoningConfig.deepThinkingTriggers.measurementChange}
                    onChange={(e) => updateField('defaultReasoningConfig.deepThinkingTriggers.measurementChange', e.target.checked)} />}
                  label="Assessment trajectory change"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={localConfig.defaultReasoningConfig.deepThinkingTriggers.clinicalDecisionPoint}
                    onChange={(e) => updateField('defaultReasoningConfig.deepThinkingTriggers.clinicalDecisionPoint', e.target.checked)} />}
                  label="Clinical decision point"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number" size="small" fullWidth
                  label="Significant Gap (days)"
                  value={localConfig.defaultReasoningConfig.deepThinkingTriggers.significantGapDays}
                  onChange={(e) => updateField('defaultReasoningConfig.deepThinkingTriggers.significantGapDays', parseInt(e.target.value) || 7)}
                  inputProps={{ min: 1 }}
                  helperText="Days of inactivity that triggers deep thinking"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Default Consultation Triggers */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Default Consultation Triggers</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number" size="small" fullWidth
                  label="Periodic Review (every N client messages)"
                  value={localConfig.defaultReasoningConfig.consultationTriggers.periodicReviewMessages}
                  onChange={(e) => updateField('defaultReasoningConfig.consultationTriggers.periodicReviewMessages', parseInt(e.target.value) || 10)}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={localConfig.defaultReasoningConfig.consultationTriggers.domainQuestionDetected}
                    onChange={(e) => updateField('defaultReasoningConfig.consultationTriggers.domainQuestionDetected', e.target.checked)} />}
                  label="Domain-specific question detected"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={localConfig.defaultReasoningConfig.consultationTriggers.formulationChange}
                    onChange={(e) => updateField('defaultReasoningConfig.consultationTriggers.formulationChange', e.target.checked)} />}
                  label="Formulation change"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={localConfig.defaultReasoningConfig.consultationTriggers.presentationShift}
                    onChange={(e) => updateField('defaultReasoningConfig.consultationTriggers.presentationShift', e.target.checked)} />}
                  label="Presentation shift"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Formulation Context */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Formulation Context</Typography>
            <TextField
              type="number" size="small" fullWidth
              label="Formulation Context Max Tokens"
              value={localConfig.defaultReasoningConfig.formulationContextMaxTokens}
              onChange={(e) => updateField('defaultReasoningConfig.formulationContextMaxTokens', parseInt(e.target.value) || 500)}
              inputProps={{ min: 100, max: 2000 }}
              helperText="Maximum tokens for formulation context injected in fast mode (100-2000)"
            />
          </Paper>

          {/* Reasoning LLM Provider */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>Reasoning LLM Provider</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              LLM provider used for internal reasoning calls (deep thinking step). Can differ from the persona's therapy provider.
              Providers and models are configured in the LLM Configuration page.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={localConfig.reasoningLlmProvider.provider}
                    onChange={(e) => {
                      updateField('reasoningLlmProvider.provider', e.target.value);
                      updateField('reasoningLlmProvider.model', '');
                    }}
                    label="Provider"
                  >
                    <MenuItem value="default">Default (use platform primary)</MenuItem>
                    {llmConfig?.availableModels && [...new Set(llmConfig.availableModels.filter(m => m.isActive).map(m => m.provider))].map(p => (
                      <MenuItem key={p} value={p}>{p === 'vertex_ai' ? 'Vertex AI' : p === 'gemini' ? 'Gemini' : p === 'openai' ? 'OpenAI' : p === 'anthropic' ? 'Anthropic' : p}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Model</InputLabel>
                  <Select
                    value={localConfig.reasoningLlmProvider.model || ''}
                    onChange={(e) => updateField('reasoningLlmProvider.model', e.target.value)}
                    label="Model"
                    disabled={localConfig.reasoningLlmProvider.provider === 'default'}
                  >
                    <MenuItem value="">Provider default</MenuItem>
                    {llmConfig?.availableModels && llmConfig.availableModels
                      .filter(m => m.isActive && (localConfig.reasoningLlmProvider.provider === 'default' || m.provider === localConfig.reasoningLlmProvider.provider))
                      .map(m => (
                        <MenuItem key={`${m.provider}-${m.id}`} value={m.id}>{m.displayName || m.id}</MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" gutterBottom>
                  Temperature: {localConfig.reasoningLlmProvider.temperature}
                </Typography>
                <Slider
                  value={localConfig.reasoningLlmProvider.temperature}
                  onChange={(e, v) => updateField('reasoningLlmProvider.temperature', v)}
                  min={0} max={2} step={0.1}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 0.3, label: '0.3' },
                    { value: 1, label: '1' },
                    { value: 2, label: '2' }
                  ]}
                />
              </Grid>
            </Grid>
          </Paper>
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          Save Gen 2 Configuration
        </Button>
      </Box>
    </Box>
  );
};

// Client UI Settings sub-component (Meeting 3 implementation).
// Edits PlatformSettings.clientUi via the existing handleUpdateSettings handler,
// which routes through PUT /api/platform-settings.
const REJECTED_DISCLAIMER_PATTERN = /replacement\s+for\s+human\s+therapy/i;
const ROLE_LABEL_OPTIONS = [
  'AI Therapist',
  'AI Assistant',
  'AI Coach',
  'AI Supporter',
  'AI Companion',
  'AI Guide'
];
const WIDGET_IDS = [
  { id: 'compactCheckIn', label: 'Compact check-in' },
  { id: 'checkIn', label: 'Check-in' },
  { id: 'checkInHistory', label: 'Check-in history' },
  { id: 'quickActions', label: 'Quick actions' },
  { id: 'goals', label: 'Active goals' },
  { id: 'weeklyProgress', label: 'Weekly progress' },
  { id: 'activityFeed', label: 'Activity feed' },
  { id: 'sessionSummary', label: 'Session summary' }
];

const ClientUiSettings = ({ settings, handleUpdateSettings, loading }) => {
  const initial = settings?.clientUi || {};
  const [formData, setFormData] = useState({
    showNavEmojis: initial.showNavEmojis ?? false,
    showPerBubbleAILabel: initial.showPerBubbleAILabel ?? true,
    perBubbleAILabelStyle: initial.perBubbleAILabelStyle || 'full',
    defaultRoleLabel: initial.defaultRoleLabel || 'AI Therapist',
    aiAvatarDisplay: initial.aiAvatarDisplay || 'header',
    representationsEnabled: initial.representationsEnabled ?? false,
    defaultRepresentationKey: initial.defaultRepresentationKey || '',
    focusModeEnabled: initial.focusModeEnabled ?? false,
    chatDisclaimerFooter: {
      enabled: initial.chatDisclaimerFooter?.enabled ?? true,
      text: initial.chatDisclaimerFooter?.text || 'AI assistant — your therapy is with your human therapist.'
    },
    aboutThisAi: {
      headline: initial.aboutThisAi?.headline || 'About this AI',
      body: initial.aboutThisAi?.body || '',
      notReplacementLine: initial.aboutThisAi?.notReplacementLine || 'The AI is an assistant to your therapy. Your therapy is with your human therapist.',
      liveVideoLine: initial.aboutThisAi?.liveVideoLine || ''
    },
    dashboardWidgetCopy: { ...(initial.dashboardWidgetCopy || {}) }
  });
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!settings?.clientUi) return;
    const u = settings.clientUi;
    setFormData({
      showNavEmojis: u.showNavEmojis ?? false,
      showPerBubbleAILabel: u.showPerBubbleAILabel ?? true,
      perBubbleAILabelStyle: u.perBubbleAILabelStyle || 'full',
      defaultRoleLabel: u.defaultRoleLabel || 'AI Therapist',
      aiAvatarDisplay: u.aiAvatarDisplay || 'header',
      representationsEnabled: u.representationsEnabled ?? false,
      defaultRepresentationKey: u.defaultRepresentationKey || '',
      focusModeEnabled: u.focusModeEnabled ?? false,
      chatDisclaimerFooter: {
        enabled: u.chatDisclaimerFooter?.enabled ?? true,
        text: u.chatDisclaimerFooter?.text || 'AI assistant — your therapy is with your human therapist.'
      },
      aboutThisAi: {
        headline: u.aboutThisAi?.headline || 'About this AI',
        body: u.aboutThisAi?.body || '',
        notReplacementLine: u.aboutThisAi?.notReplacementLine || 'The AI is an assistant to your therapy. Your therapy is with your human therapist.',
        liveVideoLine: u.aboutThisAi?.liveVideoLine || ''
      },
      dashboardWidgetCopy: { ...(u.dashboardWidgetCopy || {}) }
    });
  }, [settings]);

  const handleSave = (e) => {
    e?.preventDefault?.();
    if (REJECTED_DISCLAIMER_PATTERN.test(formData.chatDisclaimerFooter.text || '')) {
      setValidationError('Disclaimer footer text cannot use "replacement for human therapy" — AIMHI includes the human therapy via in-platform live video.');
      return;
    }
    if (REJECTED_DISCLAIMER_PATTERN.test(formData.aboutThisAi.notReplacementLine || '')) {
      setValidationError('"About this AI" not-replacement line cannot use "replacement for human therapy" wording.');
      return;
    }
    setValidationError('');
    handleUpdateSettings('clientUi', formData);
    // Tell the ClientUiProvider (mounted higher up in the same SPA) to refetch
    // immediately rather than waiting for a tab focus event.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('aimhi:clientUiSettingsChanged'));
    }
  };

  const updateWidgetCopy = (id, value) => {
    setFormData((prev) => ({
      ...prev,
      dashboardWidgetCopy: { ...prev.dashboardWidgetCopy, [id]: value }
    }));
  };

  return (
    <Box component="form" onSubmit={handleSave} sx={{ maxWidth: 900, margin: 'auto' }}>
      <Typography variant="h6" gutterBottom>Client UI</Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Controls the youth-facing chat surface, dashboard help text, and disclaimer wording.
        Service groups can override these, and therapists can override a narrower subset for
        their assigned clients. Floors: under-18 service groups cannot turn off the per-bubble
        AI label; "replacement for human therapy" wording is rejected at every layer.
      </Alert>

      {validationError && (
        <Alert severity="error" sx={{ mb: 2 }}>{validationError}</Alert>
      )}

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Chat bubble label</Typography>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="client-ui-default-role-label">Default role label</InputLabel>
        <Select
          labelId="client-ui-default-role-label"
          value={formData.defaultRoleLabel}
          label="Default role label"
          onChange={(e) => setFormData({ ...formData, defaultRoleLabel: e.target.value })}
        >
          {ROLE_LABEL_OPTIONS.map((label) => (
            <MenuItem key={label} value={label}>{label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Switch
            checked={formData.showPerBubbleAILabel}
            onChange={(e) => setFormData({ ...formData, showPerBubbleAILabel: e.target.checked })}
          />
        }
        label="Show AI label on every chat bubble"
        sx={{ display: 'block', mb: 1 }}
      />
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="client-ui-bubble-style">Per-bubble label style</InputLabel>
        <Select
          labelId="client-ui-bubble-style"
          value={formData.perBubbleAILabelStyle}
          label="Per-bubble label style"
          onChange={(e) => setFormData({ ...formData, perBubbleAILabelStyle: e.target.value })}
          disabled={!formData.showPerBubbleAILabel}
        >
          <MenuItem value="full">Full (bold caption above bubble)</MenuItem>
          <MenuItem value="subtle">Subtle (small corner tag)</MenuItem>
        </Select>
      </FormControl>

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>AI persona avatar</Typography>
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="client-ui-avatar-display">Where to show the AI avatar</InputLabel>
        <Select
          labelId="client-ui-avatar-display"
          value={formData.aiAvatarDisplay}
          label="Where to show the AI avatar"
          onChange={(e) => setFormData({ ...formData, aiAvatarDisplay: e.target.value })}
        >
          <MenuItem value="none">None — never show avatars</MenuItem>
          <MenuItem value="header">Header only — once at the top of the chat</MenuItem>
          <MenuItem value="grouped">Grouped — first message of each AI run (most chat apps)</MenuItem>
          <MenuItem value="every_message">Every AI message</MenuItem>
        </Select>
        <FormHelperText>
          "Every message" can amplify parasocial attachment in young users — prefer "header" or "grouped" for under-18 audiences.
        </FormHelperText>
      </FormControl>

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Chat footer disclaimer</Typography>
      <FormControlLabel
        control={
          <Switch
            checked={formData.chatDisclaimerFooter.enabled}
            onChange={(e) => setFormData({
              ...formData,
              chatDisclaimerFooter: { ...formData.chatDisclaimerFooter, enabled: e.target.checked }
            })}
          />
        }
        label="Show persistent disclaimer footer in chat"
        sx={{ display: 'block', mb: 1 }}
      />
      <TextField
        fullWidth
        multiline
        rows={2}
        label="Footer text"
        value={formData.chatDisclaimerFooter.text}
        onChange={(e) => setFormData({
          ...formData,
          chatDisclaimerFooter: { ...formData.chatDisclaimerFooter, text: e.target.value }
        })}
        helperText='Use {roleLabel} to insert the AI role label chosen for each service group (e.g. "{roleLabel} — your therapy is with your human therapist"). Do not type the label directly, or it will not update when a group chooses a different one. Cannot contain "replacement for human therapy" — AIMHI includes the human therapy via in-platform live video.'
        sx={{ mb: 3 }}
      />

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>About this AI panel</Typography>
      <TextField
        fullWidth
        label="Headline"
        value={formData.aboutThisAi.headline}
        onChange={(e) => setFormData({
          ...formData,
          aboutThisAi: { ...formData.aboutThisAi, headline: e.target.value }
        })}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        multiline
        rows={2}
        label="Top-line (assistant-not-therapy framing)"
        value={formData.aboutThisAi.notReplacementLine}
        onChange={(e) => setFormData({
          ...formData,
          aboutThisAi: { ...formData.aboutThisAi, notReplacementLine: e.target.value }
        })}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Body (optional)"
        value={formData.aboutThisAi.body}
        onChange={(e) => setFormData({
          ...formData,
          aboutThisAi: { ...formData.aboutThisAi, body: e.target.value }
        })}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        multiline
        rows={2}
        label="Live video line"
        value={formData.aboutThisAi.liveVideoLine}
        onChange={(e) => setFormData({
          ...formData,
          aboutThisAi: { ...formData.aboutThisAi, liveVideoLine: e.target.value }
        })}
        sx={{ mb: 3 }}
      />

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Persona representations</Typography>
      <FormControlLabel
        control={
          <Switch
            checked={formData.representationsEnabled}
            onChange={(e) => setFormData({ ...formData, representationsEnabled: e.target.checked })}
          />
        }
        label="Enable persona representations (avatar + voice bundles)"
        sx={{ display: 'block', mb: 1 }}
      />
      <FormHelperText sx={{ mb: 2 }}>
        When enabled, clients can pick a representation from the curated set in the
        "Persona Representations" tab. Leave the default key blank to show the picker
        on first chat.
      </FormHelperText>
      <TextField
        fullWidth
        label="Default representation key (optional)"
        value={formData.defaultRepresentationKey}
        onChange={(e) => setFormData({ ...formData, defaultRepresentationKey: e.target.value.toLowerCase().trim() })}
        helperText="If set, this representation is used when a client hasn't picked one (no picker on first chat). Use the key value from the Persona Representations tab."
        sx={{ mb: 3 }}
      />

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Navigation</Typography>
      <FormControlLabel
        control={
          <Switch
            checked={formData.showNavEmojis}
            onChange={(e) => setFormData({ ...formData, showNavEmojis: e.target.checked })}
          />
        }
        label="Show emoji prefixes on Dashboard / Chat / Journal nav"
        sx={{ display: 'block', mb: 2 }}
      />
      <FormControlLabel
        control={
          <Switch
            checked={formData.focusModeEnabled}
            onChange={(e) => setFormData({ ...formData, focusModeEnabled: e.target.checked })}
          />
        }
        label="Allow users to enable focus mode (hide nav)"
        sx={{ display: 'block', mb: 3 }}
      />

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Dashboard widget tooltip copy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Plain-English (i)-tooltip text shown next to each widget title. Leave a field blank to hide the info icon for that widget.
      </Typography>
      {WIDGET_IDS.map(({ id, label }) => (
        <TextField
          key={id}
          fullWidth
          label={label}
          value={formData.dashboardWidgetCopy[id] || ''}
          onChange={(e) => updateWidgetCopy(id, e.target.value)}
          sx={{ mb: 1.5 }}
        />
      ))}

      <Button
        type="submit"
        variant="contained"
        color="primary"
        startIcon={<SaveIcon />}
        sx={{ mt: 3 }}
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : 'Save Client UI Settings'}
      </Button>
    </Box>
  );
};

export default PlatformSettingsManager;
